import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import whatInput from 'what-input';
import { ListingActionTypes } from '@state/features/common.types';
import JsonLd from '@utils/JsonLd';
import { getProductAvailability } from '@utils/product';
import customEventBus from '@common/scripts/libs/eventBus';
import ListingTier from './ListingTier';
import Button from '@stone/ui/button/Button';
import { getZeroPadNumber } from '@common/utils/stringUtils';
const ListingResults = (props) => {
    const { t } = useTranslation();
    const { results, progress, count, selectedFilters, pageNumber, breadcrumbs, infiniteScroll } = useSelector((state) => state.search);
    const isLoading = useSelector((state) => state.loading).requests.includes('SEARCH');
    const [itemsNumber, setItemsNumber] = useState(0);
    const [isKeyboardIntent, setIsKeyboardIntent] = useState(false);
    const totalCards = [...results];
    const [fetchinload, setFetchinload] = useState(false);
    const { apiErrors } = useSelector((state) => state.errors);
    const listingContainer = useRef(null);
    const loadingError = useMemo(() => {
        return apiErrors.find((error) => {
            return error.code === 'PAGE';
        });
    }, [apiErrors]);
    const dispatch = useDispatch();
    useEffect(() => {
        let onKeyborardModeEnabled = (data) => {
            setIsKeyboardIntent(data.isKeyboardMode);
        };
        customEventBus.on('keyboard:mode', onKeyborardModeEnabled);
        return () => {
            customEventBus.off('keyboard:mode', onKeyborardModeEnabled);
        };
    }, []);
    const fetchData = () => {
        var _a;
        console.log('INFINITE SCROLL ENABLED: ', infiniteScroll);
        setItemsNumber((_a = listingContainer.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('#plp_tilelist > li').length);
        dispatch({
            type: ListingActionTypes.FETCH_LISTING,
            payload: {
                resultsType: 'PAGE',
                start: progress,
                selectedFilters,
                infinitescroll: true,
            },
        });
    };
    const hasMore = useMemo(() => {
        return progress < count;
    }, [progress, count]);
    useEffect(() => {
        if (whatInput.ask() == 'keyboard' && pageNumber > 0) {
            setTimeout(() => document.querySelector(`#plp_tilelist > li:nth-child(${itemsNumber + 1}) .product-tile`).focus(), 500);
        }
    }, [pageNumber]);
    const loader = () => {
        return (!loadingError &&
            !isKeyboardIntent && (React.createElement("div", { className: "container" },
            React.createElement("div", { className: "plp-loading", "aria-live": "polite" },
                React.createElement("div", { className: "wrapper-loading" },
                    React.createElement("span", null, t('plp.loading')),
                    React.createElement("i", { className: "progress-bar", style: { width: `${((progress * 100) / count).toFixed(2)}%` } }))))));
    };
    const error = () => {
        if (loadingError && !isLoading) {
            return (React.createElement("div", { className: "container" },
                React.createElement("span", { className: "plp-error", "aria-live": "polite" }, t('plp.error'))));
        }
        return React.createElement(React.Fragment, null);
    };
    const showAccessibleLoadMore = () => {
        if (!!fetchinload || (results.length && hasMore && (isKeyboardIntent || !infiniteScroll))) {
            return (React.createElement("div", { className: "container loadmore" },
                React.createElement("div", { className: "plp-a11y__loadmore loadmore__inner" },
                    React.createElement("h2", { className: "visually-hidden" },
                        " ",
                        t('plp.moreproducts')),
                    React.createElement(Button, { type: "button", isLoading: fetchinload, cls: ['button--secondary'], className: "full", onClick: fetchData },
                        React.createElement("span", null, t('plp.loadmore'))))));
        }
        return React.createElement(React.Fragment, null);
    };
    const schemaDataFc = (totalCards) => {
        return {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            url: location.href,
            numberOfItems: totalCards === null || totalCards === void 0 ? void 0 : totalCards.length,
            itemListElement: totalCards === null || totalCards === void 0 ? void 0 : totalCards.map((product) => {
                var _a, _b, _c, _d, _e;
                return ({
                    '@type': 'Product',
                    name: product.productName,
                    image: (_a = product.imgs) === null || _a === void 0 ? void 0 : _a.urls,
                    description: product === null || product === void 0 ? void 0 : product.shortDescription,
                    brand: {
                        '@type': 'Brand',
                        name: t('seo.sitename'),
                    },
                    offers: {
                        url: product.productUrl,
                        '@type': 'Offer',
                        priceCurrency: (_c = (_b = product.price) === null || _b === void 0 ? void 0 : _b.sales) === null || _c === void 0 ? void 0 : _c.currency,
                        price: (_e = (_d = product.price) === null || _d === void 0 ? void 0 : _d.sales) === null || _e === void 0 ? void 0 : _e.value,
                        itemCondition: 'https://schema.org/NewCondition',
                        availability: getProductAvailability(product),
                    },
                });
            }),
        };
    };
    const schemaData = useMemo(() => (totalCards ? schemaDataFc(totalCards) : undefined), [totalCards]);
    const getBreadcrumbSchemaData = () => {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((item, index) => ({
                '@type': 'ListItem',
                position: ++index,
                name: item.htmlValue,
                item: location.origin + item.url,
            })),
        };
    };
    const breadcrumbSchemaData = useMemo(() => (breadcrumbs ? getBreadcrumbSchemaData() : undefined), [breadcrumbs]);
    return (React.createElement(React.Fragment, null,
        schemaData && React.createElement(JsonLd, { data: schemaData }),
        breadcrumbSchemaData && React.createElement(JsonLd, { data: breadcrumbSchemaData }),
        (totalCards === null || totalCards === void 0 ? void 0 : totalCards.length) > 0 ? (React.createElement(InfiniteScroll, { style: isLoading && {
                minHeight: '100wh',
            }, dataLength: results.length, next: fetchData, hasMore: !isKeyboardIntent && infiniteScroll && hasMore && !isLoading, loader: loader() },
            React.createElement("div", { role: "region", className: "container", ref: listingContainer },
                React.createElement("p", { className: "plp-listing__counter", role: "status", "aria-live": "polite" },
                    React.createElement("span", { "aria-hidden": "true", className: "plp-listing__counter-count" }, `${getZeroPadNumber(count)} `),
                    React.createElement("span", { className: "visually-hidden" }, `${count} `),
                    React.createElement("span", null, t('label.products'))),
                React.createElement("ul", { className: classNames('grid-container'), id: "plp_tilelist", "aria-label": t('label.products'), tabIndex: -1 },
                    React.createElement(ListingTier, { pageType: "PLP", tier: props.tier }))),
            showAccessibleLoadMore(),
            error())) : (React.createElement("p", { className: "font__title text-center" }, t('search.noresults.title')))));
};
export default ListingResults;
